/**
 * Paint Call Review transcript text from stored findings.
 * Locations come from segment id, start/end timestamps, or the stored quote.
 * Overlapping severities keep the highest: critical > serious > warning.
 */

export type TranscriptSeverity = 'warning' | 'serious' | 'critical';

export type TranscriptHighlightSegment = {
  id: string;
  start_ms: number;
  end_ms: number;
  text?: string | null;
};

export type TranscriptFindingMark = {
  severity?: string | null;
  title?: string | null;
  start_ms?: number | null;
  end_ms?: number | null;
  segment_id?: string | null;
  transcript_excerpt?: string | null;
  matched_phrase?: string | null;
  review_status?: string | null;
  status?: string | null;
};

export type TranscriptHighlightSpan = {
  start: number;
  end: number;
  severity: TranscriptSeverity;
  title: string;
};

/** Solid swatches aligned with Calls page badges.
 * Warning matches the warning chip (MUI warning.main).
 * Serious uses the Call QA serious orange so it stays distinct from critical.
 * Critical matches the error chip used for critical findings.
 */
export const TRANSCRIPT_SEVERITY_STYLE: Record<
  TranscriptSeverity,
  { swatch: string; background: string }
> = {
  warning: { swatch: '#ed6c02', background: 'rgba(237, 108, 2, 0.38)' },
  serious: { swatch: '#EA580C', background: 'rgba(234, 88, 12, 0.40)' },
  critical: { swatch: '#d32f2f', background: 'rgba(211, 47, 47, 0.34)' },
};

const SEVERITY_RANK: Record<TranscriptSeverity, number> = {
  warning: 1,
  serious: 2,
  critical: 3,
};

function asSeverity(raw?: string | null): TranscriptSeverity | null {
  const s = String(raw || '').toLowerCase();
  if (s === 'warning' || s === 'serious' || s === 'critical') return s;
  return null;
}

function isDismissed(finding: TranscriptFindingMark): boolean {
  const status = String(finding.review_status || finding.status || 'OPEN').toUpperCase();
  return status === 'DISMISSED';
}

function finiteMs(value: number | null | undefined): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

type CharRange = { start: number; end: number };

function findDirectRanges(text: string, quote: string): CharRange[] {
  const needle = quote.trim().toLowerCase();
  if (needle.length < 3) return [];
  const hay = text.toLowerCase();
  const ranges: CharRange[] = [];
  let from = 0;
  while (from <= hay.length - needle.length) {
    const idx = hay.indexOf(needle, from);
    if (idx < 0) break;
    ranges.push({ start: idx, end: idx + needle.length });
    from = idx + needle.length;
  }
  return ranges;
}

/** Case-insensitive match that also collapses whitespace runs. */
function findQuoteRanges(text: string, quote: string): CharRange[] {
  const direct = findDirectRanges(text, quote);
  if (direct.length) return direct;

  const needle = quote.trim().toLowerCase().replace(/\s+/g, ' ');
  if (needle.length < 3 || !text) return [];

  const map: number[] = [];
  let norm = '';
  let i = 0;
  while (i < text.length) {
    if (/\s/.test(text[i])) {
      const start = i;
      while (i < text.length && /\s/.test(text[i])) i += 1;
      if (!norm.length || i >= text.length) continue;
      norm += ' ';
      map.push(start);
      continue;
    }
    norm += text[i].toLowerCase();
    map.push(i);
    i += 1;
  }

  const ranges: CharRange[] = [];
  let from = 0;
  while (from <= norm.length - needle.length) {
    const idx = norm.indexOf(needle, from);
    if (idx < 0) break;
    const endIdx = idx + needle.length - 1;
    ranges.push({ start: map[idx], end: map[endIdx] + 1 });
    from = idx + needle.length;
  }
  return ranges;
}

function textCoveredByQuote(text: string, quote: string): boolean {
  const body = text.trim();
  if (body.length < 12) return false;
  const normBody = body.toLowerCase().replace(/\s+/g, ' ');
  const normQuote = quote.toLowerCase().replace(/\s+/g, ' ');
  return normQuote.includes(normBody);
}

function quoteRangesInText(text: string, finding: TranscriptFindingMark): CharRange[] | null {
  const excerpt = String(finding.transcript_excerpt || '').trim();
  const phrase = String(finding.matched_phrase || '').trim();
  if (excerpt.length >= 3) {
    const ranges = findQuoteRanges(text, excerpt);
    if (ranges.length) return ranges;
    if (textCoveredByQuote(text, excerpt)) return [{ start: 0, end: text.length }];
  }
  if (phrase.length >= 3 && phrase.toLowerCase() !== excerpt.toLowerCase()) {
    const ranges = findQuoteRanges(text, phrase);
    if (ranges.length) return ranges;
  }
  return null;
}

function segmentsForFinding(
  segments: TranscriptHighlightSegment[],
  finding: TranscriptFindingMark,
): TranscriptHighlightSegment[] {
  const byId = finding.segment_id
    ? segments.filter((seg) => seg.id === finding.segment_id)
    : [];
  const start = finiteMs(finding.start_ms);
  const endRaw = finiteMs(finding.end_ms);
  let timed: TranscriptHighlightSegment[] = [];
  if (start != null) {
    const end = endRaw == null || endRaw <= start ? start : endRaw;
    const point = end === start;
    timed = segments.filter((seg) => {
      const segStart = Number(seg.start_ms) || 0;
      const segEnd = Number(seg.end_ms) || 0;
      if (point) return start >= segStart && start < segEnd;
      return start < segEnd && end > segStart;
    });
    if (point && timed.length === 0) {
      const boundary = segments.filter((seg) => Number(seg.end_ms) === start);
      if (boundary.length) timed = [boundary[boundary.length - 1]];
    }
  }
  const seen = new Set<string>();
  const combined: TranscriptHighlightSegment[] = [];
  for (const seg of [...byId, ...timed]) {
    if (seen.has(seg.id)) continue;
    seen.add(seg.id);
    combined.push(seg);
  }
  return combined;
}

type Interval = CharRange & { severity: TranscriptSeverity; title: string; rank: number };

function paintText(text: string, intervals: Interval[]): TranscriptHighlightSpan[] {
  if (!text || intervals.length === 0) return [];
  const cells: Array<{ rank: number; severity: TranscriptSeverity; titles: string[] } | null> = new Array(text.length).fill(null);
  for (const interval of intervals) {
    const start = Math.max(0, Math.min(text.length, interval.start));
    const end = Math.max(start, Math.min(text.length, interval.end));
    for (let i = start; i < end; i += 1) {
      const current = cells[i];
      if (!current || interval.rank > current.rank) {
        cells[i] = {
          rank: interval.rank,
          severity: interval.severity,
          titles: interval.title ? [interval.title] : [],
        };
      } else if (interval.rank === current.rank && interval.title && !current.titles.includes(interval.title)) {
        current.titles.push(interval.title);
      }
    }
  }

  const spans: TranscriptHighlightSpan[] = [];
  let i = 0;
  while (i < text.length) {
    const cell = cells[i];
    if (!cell) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < text.length) {
      const next = cells[j];
      if (!next || next.severity !== cell.severity || next.titles.join('\n') !== cell.titles.join('\n')) break;
      j += 1;
    }
    spans.push({
      start: i,
      end: j,
      severity: cell.severity,
      title: cell.titles.join('; '),
    });
    i = j;
  }
  return spans;
}

export function highlightTranscriptByFindings(
  segments: TranscriptHighlightSegment[],
  findings: TranscriptFindingMark[],
  options: { analysisComplete: boolean },
): Record<string, TranscriptHighlightSpan[]> {
  if (!options.analysisComplete || !segments.length || !findings.length) return {};

  const buckets = new Map<string, Interval[]>();
  const ensure = (id: string) => {
    let list = buckets.get(id);
    if (!list) {
      list = [];
      buckets.set(id, list);
    }
    return list;
  };

  for (const finding of findings) {
    if (isDismissed(finding)) continue;
    const severity = asSeverity(finding.severity);
    if (!severity) continue;
    const title = String(finding.title || '').trim();
    const rank = SEVERITY_RANK[severity];
    const located = segmentsForFinding(segments, finding);

    const pushRanges = (seg: TranscriptHighlightSegment, ranges: CharRange[]) => {
      const text = String(seg.text || '');
      if (!text) return;
      const bucket = ensure(seg.id);
      for (const range of ranges) {
        if (range.end <= range.start) continue;
        bucket.push({ ...range, severity, title, rank });
      }
    };

    if (located.length) {
      const quoted: Array<{ seg: TranscriptHighlightSegment; ranges: CharRange[] }> = [];
      for (const seg of located) {
        const ranges = quoteRangesInText(String(seg.text || ''), finding);
        if (ranges?.length) quoted.push({ seg, ranges });
      }
      if (quoted.length) {
        quoted.forEach(({ seg, ranges }) => pushRanges(seg, ranges));
      } else {
        located.forEach((seg) => {
          const text = String(seg.text || '');
          if (text) pushRanges(seg, [{ start: 0, end: text.length }]);
        });
      }
      continue;
    }

    for (const seg of segments) {
      const ranges = quoteRangesInText(String(seg.text || ''), finding);
      if (ranges?.length) pushRanges(seg, ranges);
    }
  }

  const painted: Record<string, TranscriptHighlightSpan[]> = {};
  buckets.forEach((intervals, id) => {
    const seg = segments.find((item) => item.id === id);
    const spans = paintText(String(seg?.text || ''), intervals);
    if (spans.length) painted[id] = spans;
  });
  return painted;
}

export function highestHighlightSeverity(
  spans: TranscriptHighlightSpan[] | undefined,
): TranscriptSeverity | null {
  if (!spans?.length) return null;
  return spans.reduce<TranscriptSeverity>((best, span) => (
    SEVERITY_RANK[span.severity] > SEVERITY_RANK[best] ? span.severity : best
  ), spans[0].severity);
}
